import type { CardSpendRow } from '../types/analytics';
import type { SpendCategoryNode } from '../types/financial';

const CHASE_SLUGS = new Set(['dining', 'entertainment', 'shopping', 'transport']);
const AMEX_SLUGS = new Set(['groceries', 'household', 'health']);

function nextMonthDueDate(periodKey: string): string {
  const [year, month] = periodKey.split('-').map(Number);
  if (!year || !month) return `${periodKey}-25`;
  const due = new Date(Date.UTC(year, month, 25));
  return due.toISOString().slice(0, 10);
}

function minPaymentFor(balance: number): number {
  return Math.max(35, Math.round(balance * 0.02 * 100) / 100);
}

/** Sample trees omit `transactionCount` — count nested merchant txs instead. */
function categoryTxCount(category: SpendCategoryNode): number {
  if (category.transactionCount != null && category.transactionCount > 0) {
    return category.transactionCount;
  }
  return category.merchants.reduce((sum, merchant) => sum + merchant.transactions.length, 0);
}

function toCard(
  id: string,
  label: string,
  last4: string,
  issuer: string,
  type: CardSpendRow['type'],
  categories: SpendCategoryNode[],
  periodKey: string,
): CardSpendRow | null {
  const totalSpend = categories.reduce((sum, c) => sum + c.amount, 0);
  const transactionCount = categories.reduce((sum, c) => sum + categoryTxCount(c), 0);
  if (totalSpend <= 0) return null;
  const isCredit = type === 'credit_card';
  const statementBalance = isCredit ? Math.round(totalSpend * 1.08 * 100) / 100 : null;
  return {
    spendAccountId: id,
    label,
    type,
    last4,
    issuer,
    totalSpend,
    transactionCount,
    statementBalance,
    previousBalance: isCredit && statementBalance != null
      ? Math.round((statementBalance - totalSpend) * 100) / 100
      : null,
    minimumPayment: isCredit && statementBalance != null ? minPaymentFor(statementBalance) : null,
    paymentDueDate: isCredit ? nextMonthDueDate(periodKey) : null,
    statementPeriod: periodKey,
    categories: categories.map((c) => ({
      categorySlug: c.id,
      categoryName: c.name,
      totalAmount: c.amount,
      transactionCount: categoryTxCount(c),
    })),
  };
}

/** Preview/sample cards derived from the fixture spend tree. */
export function buildSampleCreditCards(
  periodKey: string,
  categories: SpendCategoryNode[],
): CardSpendRow[] {
  const chase = categories.filter((c) => CHASE_SLUGS.has(c.id));
  const amex = categories.filter((c) => AMEX_SLUGS.has(c.id));
  const used = new Set([...CHASE_SLUGS, ...AMEX_SLUGS]);
  const debit = categories.filter((c) => !used.has(c.id));

  return [
    toCard('sample_chase_sapphire', 'Chase Sapphire Preferred', '4242', 'Chase', 'credit_card', chase, periodKey),
    toCard('sample_amex_gold', 'Amex Gold', '1008', 'American Express', 'credit_card', amex, periodKey),
    toCard('sample_chase_debit', 'Chase Debit', '8821', 'Chase', 'debit_card', debit, periodKey),
  ].filter((card): card is CardSpendRow => card != null);
}
